import { useLocation } from "react-router-dom";
import { canAccessGroupSummary } from "@/lib/sale-permissions";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Check,
  Close,
  ContentCopy,
  LockOutlined,
  Refresh,
  Telegram,
} from "@mui/icons-material";
import { useCallback, useEffect, useRef, useState, useSyncExternalStore, type ReactNode } from "react";
import { notifyAuthSessionChanged, subscribeAuthSession } from "@/lib/auth-session";
import { AuthenticatedUserProvider } from "@/contexts/AuthenticatedUserContext";
import { readAuthenticatedSession, replaceAuthenticatedSession, clearAuthenticatedSessions, sessionKey } from "@/lib/authenticated-session";
import { browserTelegramInitData, telegramStaffAuthentication } from "@/utils/telegramIdentity";
import { useTranslation } from "react-i18next";
import { getAuthStorageKeys } from "@/lib/auth-storage";
import { API_BASE } from "@/env.mapper";
import { getTelegramLaunchData, useTelegramLaunchInfo } from "@/hooks/useTelegramLaunchInfo";

export default function TelegramLoginGate({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const launchInfo = useTelegramLaunchInfo();
  const keys = getAuthStorageKeys();
  const location = useLocation();
  const token = useSyncExternalStore(subscribeAuthSession, () => `${localStorage.getItem(keys.access) || ""}:${localStorage.getItem(sessionKey(keys.access)) || ""}`, () => "");
  const currentSession = readAuthenticatedSession(keys.access);
  const hasToken = Boolean(currentSession);
  const groupSummaryAccess = canAccessGroupSummary(currentSession, location.pathname);

  // Cached sessions must not bypass fresh role and permission checks on launch.
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isUnauthorized, setIsUnauthorized] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const autoAttemptedRef = useRef(false);

  // Ensure session is marked as sale-support
  useEffect(() => {
    if (typeof window !== "undefined") {
      sessionStorage.setItem("active_mini_app", "sale-support");
    }
  }, []);

  const login = useCallback(async () => {
    setAuthenticated(false);
    setLoading(true);
    setError("");
    setIsUnauthorized(false);
    clearAuthenticatedSessions();
    notifyAuthSessionChanged();
    try {
      if (typeof window !== "undefined" && window.Telegram?.WebApp) {
        try {
          window.Telegram.WebApp.ready();
          window.Telegram.WebApp.expand();
        } catch {
          // ignore
        }
      }

      const launch = getTelegramLaunchData();
      const initData = browserTelegramInitData(launch.rawLaunchParams);
      const identity = telegramStaffAuthentication(initData);
      const telegramId = identity.telegramUserId;
      const proofHeaders = identity.headers;

      if (!telegramId || !Number.isSafeInteger(telegramId) || telegramId <= 0) {
        throw new Error(t("telegramOpenApp", { defaultValue: "Please open this app inside Telegram" }));
      }

      const cleanBase = (API_BASE || "").replace(/\/$/, "");
      const loginPayload = JSON.stringify({ telegramChatId: telegramId });
      const loginHeaders = { "Content-Type": "application/json", ...proofHeaders };

      let response: Response;
      try {
        response = await fetch(`${cleanBase}/api/auth/telegram/login`, {
          method: "POST",
          headers: loginHeaders,
          body: loginPayload,
        });
        if (response.status === 404) {
          response = await fetch(`${cleanBase}/auth/telegram/login`, {
            method: "POST",
            headers: loginHeaders,
            body: loginPayload,
          });
        }
      } catch {
        response = await fetch(`${cleanBase}/auth/telegram/login`, {
          method: "POST",
          headers: loginHeaders,
          body: loginPayload,
        });
      }

      const resJson = await response.json().catch(() => null);

      if (response.status === 401) {
        // Unknown, inactive, or duplicate Telegram ID: no tokens issued, account not authorized
        clearAuthenticatedSessions();
        notifyAuthSessionChanged();
        setIsUnauthorized(true);
        setError(resJson?.message || t("telegramUnauthorizedMsg", { defaultValue: "Authentication error: Invalid Telegram credentials" }));
        return;
      }

      if (!response.ok) {
        const message = resJson?.message || resJson?.error || t("telegramLoginFailed", { defaultValue: "Telegram login failed." });
        throw new Error(message);
      }

      const token = resJson?.data?.accessToken || resJson?.data?.token || resJson?.token;
      const refreshToken = resJson?.data?.refreshToken || resJson?.refreshToken;

      if (!token) {
        throw new Error(t("telegramLoginFailed", { defaultValue: "Telegram login failed." }));
      }

      replaceAuthenticatedSession({ accessToken: resJson?.data?.accessToken || token, refreshToken,
        user: resJson?.data?.user, permissions: resJson?.data?.permissions, sidebar: resJson?.data?.sidebar, authorization: { kind: "staff" } }, true);
      sessionStorage.setItem("active_mini_app", "sale-support");
      notifyAuthSessionChanged();
      setAuthenticated(true);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : t("telegramLoginFailed", { defaultValue: "Telegram login failed." }));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (groupSummaryAccess) return;
    if (!authenticated && !autoAttemptedRef.current) {
      const launch = getTelegramLaunchData();
      const telegramId = launch.telegramId || launch.user?.id;
      if (telegramId) {
        autoAttemptedRef.current = true;
        void login();
      } else {
        setLoading(false);
      }
    }
  }, [authenticated, login, launchInfo, groupSummaryAccess]);

  const handleCopyTelegramId = (idVal: number | string) => {
    try {
      void navigator.clipboard.writeText(String(idVal));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  };

  const handleCloseTelegramApp = () => {
    try {
      window.Telegram?.WebApp?.close?.();
    } catch {
      // ignore
    }
  };

  if (groupSummaryAccess || (authenticated && hasToken)) return <AuthenticatedUserProvider key={token}>{children}</AuthenticatedUserProvider>;

  const launchData = getTelegramLaunchData();
  const telegramId = launchData.telegramId || launchData.user?.id;
  const displayName = [launchData.user?.firstName, launchData.user?.lastName].filter(Boolean).join(" ");
  const username = launchData.user?.username;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2.5,
        bgcolor: "background.default",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Background Gold Glow matching main mini app */}
      <Box
        sx={{
          position: "absolute",
          width: "320px",
          height: "320px",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(196, 140, 58, 0.16) 0%, rgba(196, 140, 58, 0) 70%)",
          pointerEvents: "none",
        }}
      />

      <Card
        className="glass"
        sx={{
          width: "100%",
          maxWidth: "400px",
          borderRadius: "14px",
          border: "1px solid rgba(196, 140, 58, 0.25)",
          p: 2.5,
          position: "relative",
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            pt: 1,
            pb: 1,
          }}
        >
          <Box sx={{ width: 80, height: 80, mb: 1.5 }}>
            <img
              src="/e-theap-logo.png"
              alt="E-THEAP Logo"
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
            />
          </Box>
          <Typography
            variant="h5"
            className="gold-gradient-text"
            sx={{ fontWeight: 700 }}
          >
            Sale Support
          </Typography>
        </Box>

        <CardContent sx={{ pt: 1, px: 0, pb: "0 !important" }}>
          {isUnauthorized ? (
            <Stack spacing={2} alignItems="center">
              {/* Gold Circular Badge with Lock Icon */}
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  bgcolor: "rgba(196, 140, 58, 0.12)",
                  border: "1px solid rgba(196, 140, 58, 0.35)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "primary.main",
                  mt: 0.5,
                }}
              >
                <LockOutlined sx={{ fontSize: 32 }} />
              </Box>

              <Box sx={{ textAlign: "center" }}>
                <Typography variant="h6" fontWeight={700}>
                  {t("unauthorizedAccess", { defaultValue: "Unauthorized Access" })}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "error.main",
                    mt: 0.75,
                    px: 1,
                    py: 0.5,
                    borderRadius: "6px",
                    bgcolor: "rgba(239, 68, 68, 0.08)",
                    border: "1px solid rgba(239, 68, 68, 0.2)",
                    fontSize: "0.82rem",
                    fontWeight: 500,
                  }}
                >
                  {error || t("telegramUnauthorizedMsg", { defaultValue: "Authentication error: Invalid Telegram credentials" })}
                </Typography>
              </Box>

              {/* Telegram Credentials Container */}
              <Box
                sx={{
                  width: "100%",
                  p: 1.75,
                  borderRadius: "10px",
                  bgcolor: "rgba(255, 255, 255, 0.03)",
                  border: "1px solid rgba(255, 255, 255, 0.08)",
                }}
              >
                {displayName && (
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Name
                    </Typography>
                    <Typography variant="caption" fontWeight={600}>
                      {displayName}
                    </Typography>
                  </Box>
                )}
                {username && (
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Username
                    </Typography>
                    <Typography variant="caption" fontWeight={600}>
                      @{username}
                    </Typography>
                  </Box>
                )}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Telegram ID
                  </Typography>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography
                      variant="caption"
                      sx={{
                        fontFamily: "monospace",
                        bgcolor: "rgba(196, 140, 58, 0.15)",
                        color: "#C48C3A",
                        px: 1,
                        py: 0.35,
                        borderRadius: "6px",
                        fontWeight: 700,
                        fontSize: "0.85rem",
                      }}
                    >
                      {telegramId || "Unknown"}
                    </Typography>
                    {telegramId && (
                      <Tooltip title={copied ? t("copied", { defaultValue: "Copied" }) : "Copy ID"}>
                        <IconButton
                          size="small"
                          onClick={() => handleCopyTelegramId(telegramId)}
                          sx={{
                            color: copied ? "success.main" : "text.secondary",
                            p: 0.5,
                          }}
                        >
                          {copied ? <Check sx={{ fontSize: 16 }} /> : <ContentCopy sx={{ fontSize: 16 }} />}
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </Box>
              </Box>

              <Typography
                variant="caption"
                color="text.secondary"
                textAlign="center"
                sx={{ px: 1, lineHeight: 1.5 }}
              >
                {t("telegramUnauthorizedHint", {
                  defaultValue:
                    "This Telegram account is not registered, inactive, or unlinked. Please share your Telegram ID with the administrator to get access.",
                })}
              </Typography>

              <Stack spacing={1.25} sx={{ width: "100%", pt: 1 }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => void login()}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <Refresh />}
                  sx={{
                    background: "linear-gradient(135deg, #C48C3A 0%, #E2B768 100%)",
                    color: "#000",
                    fontWeight: 700,
                    py: 1.2,
                    borderRadius: "10px",
                    textTransform: "none",
                    "&:hover": {
                      background: "linear-gradient(135deg, #b37e33 0%, #d4a95b 100%)",
                    },
                  }}
                >
                  {t("tryAgain", { defaultValue: "Try Again" })}
                </Button>

                {typeof window !== "undefined" && window.Telegram?.WebApp && (
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={handleCloseTelegramApp}
                    startIcon={<Close />}
                    sx={{
                      borderColor: "rgba(255, 255, 255, 0.15)",
                      color: "text.secondary",
                      borderRadius: "10px",
                      textTransform: "none",
                      py: 1,
                      "&:hover": {
                        borderColor: "rgba(255, 255, 255, 0.3)",
                        bgcolor: "rgba(255, 255, 255, 0.05)",
                      },
                    }}
                  >
                    {t("closeApp", { defaultValue: "Close App" })}
                  </Button>
                )}
              </Stack>
            </Stack>
          ) : (
            <Stack spacing={2.5} alignItems="center" sx={{ width: "100%", pt: 1 }}>
              {loading && (
                <Stack spacing={2} alignItems="center" sx={{ py: 2 }}>
                  <CircularProgress size={36} sx={{ color: "#C48C3A" }} />
                  <Typography variant="body2" color="text.secondary">
                    {t("telegramConnecting", { defaultValue: "Connecting with Telegram..." })}
                  </Typography>
                </Stack>
              )}

              {error && <Alert severity="error" sx={{ width: "100%" }}>{error}</Alert>}

              {(!loading || error) && (
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => void login()}
                  disabled={loading}
                  fullWidth
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Telegram />}
                  sx={{
                    backgroundColor: "#229ED9",
                    "&:hover": { backgroundColor: "#1e8ec3" },
                    borderRadius: "10px",
                    textTransform: "none",
                    py: 1.2,
                  }}
                >
                  {t("continueWithTelegram", { defaultValue: "Continue with Telegram" })}
                </Button>
              )}
            </Stack>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
